import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ChevronsUpDown } from 'lucide-react';

/**
 * Typeahead combobox for the "Saved Contact" selectors on the Service Request
 * form (Repair Site / Service Requestor / Responsible Billing Party).
 *
 * `options`     – list of contact records ({ id, ... }).
 * `primary`     – fn(option) returning the displayed primary label (e.g. company).
 * `secondary`   – fn(option) returning the displayed secondary subtitle (e.g. contact name).
 * `onSelect`    – fn(id) called when the user picks an option.
 *
 * Keyboard support, real-time filtering, Escape-to-close, and the "No results
 * found" empty state are all handled by Shadcn's underlying Command primitive.
 */
export function SavedContactCombobox({
  options,
  primary,
  secondary,
  onSelect,
  selectedId,
  placeholder,
  emptyMessage = 'No saved contacts',
  searchPlaceholder = 'Type to search…',
  triggerId,
  testId,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const safeOptions = Array.isArray(options) ? options : [];
  const selectedOpt = selectedId ? safeOptions.find((o) => o.id === selectedId) : null;
  const selectedLabel = selectedOpt
    ? `${primary(selectedOpt) || ''}${secondary && secondary(selectedOpt) ? ` — ${secondary(selectedOpt)}` : ''}`
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={triggerId}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${selectedLabel ? 'text-[#323033]' : 'text-[#5A5A5A]'}`}
          data-testid={testId}
          disabled={disabled || safeOptions.length === 0}
        >
          <span className="truncate">
            {selectedLabel || (safeOptions.length === 0 ? emptyMessage : placeholder)}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No results found</CommandEmpty>
            <CommandGroup>
              {safeOptions.map((opt) => {
                const p = primary(opt) || '';
                const s = secondary ? secondary(opt) || '' : '';
                // Combine into one searchable string so CommandInput matches on either.
                const searchValue = `${p} ${s}`.trim();
                return (
                  <CommandItem
                    key={opt.id}
                    value={searchValue}
                    onSelect={() => {
                      setOpen(false);
                      onSelect(opt.id);
                    }}
                    data-testid={`${testId}-option-${opt.id}`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-[#323033]">{p}</span>
                      {s && <span className="truncate text-xs text-[#808080]">{s}</span>}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SavedContactCombobox;
